import { PathApi } from '@udecode/plate';
import { usePluginOption } from '@udecode/plate/react';
import { streamInsertChunk, withAIBatch } from '@udecode/plate-ai';
import type { AIChatPluginConfig } from '@udecode/plate-ai/react';
import {
  AIChatPlugin as AIChatPluginBase,
  AIPlugin,
  getEditorPrompt,
  submitAIChat,
  useChatChunk,
} from '@udecode/plate-ai/react';
import type { ChatRequestOptions } from 'ai';

import { AIMenu } from '../../../ui/ai-menu';
import { cursorOverlayPlugin } from '../cursor-overlay-plugin';
import { markdownPlugin } from '../markdown-plugin';
import type { AIChatPluginType, SubmitOption } from './types';

const systemCommon = '';

const systemDefault = `\
${systemCommon}
`;

const systemSelecting = `\
${systemCommon}
`;

const systemBlockSelecting = `\
${systemCommon}
`;

const userDefault = '{prompt}';

const userSelecting = `{prompt}`;

const userBlockSelecting = `{prompt}`;

export const PROMPT_TEMPLATES = {
  systemBlockSelecting,
  systemDefault,
  systemSelecting,
  userBlockSelecting,
  userDefault,
  userSelecting,
};

export const AIChatPlugin: AIChatPluginType = AIChatPluginBase.configure({
  options: {
    promptTemplate: ({ isBlockSelecting, isSelecting }) => {
      return isBlockSelecting
        ? PROMPT_TEMPLATES.userBlockSelecting
        : isSelecting
          ? PROMPT_TEMPLATES.userSelecting
          : PROMPT_TEMPLATES.userDefault;
    },
    systemTemplate: ({ isBlockSelecting, isSelecting }) => {
      return isBlockSelecting
        ? PROMPT_TEMPLATES.systemBlockSelecting
        : isSelecting
          ? PROMPT_TEMPLATES.systemSelecting
          : PROMPT_TEMPLATES.systemDefault;
    },
  },
  render: {
    afterEditable: () => <AIMenu plugin={AIChatPlugin} />,
  },
})
  .extend({
    useHooks: ({ editor, getOption }) => {
      const mode = usePluginOption(
        { key: 'aiChat' } as AIChatPluginConfig,
        'mode'
      );

      useChatChunk({
        onChunk: ({ chunk, isFirst, nodes }) => {
          if (isFirst && mode === 'insert') {
            editor.tf.withoutSaving(() => {
              editor.tf.insertNodes(
                {
                  children: [{ text: '' }],
                  type: AIChatPluginBase.key,
                },
                {
                  at: PathApi.next(
                    editor.selection?.focus.path.slice(0, 1) ?? [0]
                  ),
                }
              );
            });
            editor.setOption(AIChatPluginBase, 'streaming', true);
          }

          if (mode === 'insert' && nodes.length > 0) {
            withAIBatch(
              editor,
              () => {
                if (!getOption('streaming')) return;
                editor.tf.withScrolling(() => {
                  streamInsertChunk(editor, chunk, {
                    textProps: {
                      ai: true,
                    },
                  });
                });
              },
              { split: isFirst }
            );
          }
        },
        onFinish: () => {
          editor.setOption(AIChatPluginBase, 'streaming', false);
          editor.setOption(AIChatPluginBase, '_blockChunks', '');
          editor.setOption(AIChatPluginBase, '_blockPath', null);
        },
      });
    },
    options: {
      lastSumbit: undefined as SubmitOption | undefined,
    },
  })
  .extendApi(({ editor, getOptions }) => ({
    submit: (options?: SubmitOption) => {
      editor.setOption(AIChatPlugin, 'lastSumbit', options);

      submitAIChat(editor, options);
    },
    reload: (options?: ChatRequestOptions) => {
      const { chat, mode } = getOptions();
      const reloadOptions =
        options ?? editor.getOption(AIChatPlugin, 'lastSumbit')?.options;

      if (mode === 'insert') {
        editor.getTransforms(AIPlugin).ai.undo();
      }

      chat
        .reload?.({
          body: {
            system: getEditorPrompt(editor, {
              promptTemplate: getOptions().systemTemplate,
            }),
          },
          ...reloadOptions,
        })
        .catch(console.error);
    },
  }));

export const aiPlugins = [
  cursorOverlayPlugin,
  markdownPlugin,
  AIPlugin,
  AIChatPlugin,
] as const;
